import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { components, createFilter } from 'react-select';
import { ErrorMessage, useField } from 'formik';
import { ReactComponent as FilledChevronDown } from '../../assets/images/svg/filled-chevron-down.svg';
import {
  InputError,
  InputHeader,
  InputLabel,
  InputLink,
  InputWrap,
} from './Components/InputComponents';
import { SelectInput } from './Components/SelectComponent';

const SelectField = ({
  label,
  disabled,
  options,
  link,
  defaultSelected = null,
  dropdownFullHeight,
  selectOption,
  setAddressInline,
  ...props
}) => {
  const [field, meta, helpers] = useField(props);

  const filterConfig = {
    ignoreCase: true,
    ignoreAccents: true,
    trim: true,
    matchFrom: 'start',
  };

  useEffect(() => {
    if (defaultSelected) {
      helpers.setValue(defaultSelected);
    }
  }, [defaultSelected]); // eslint-disable-line

  const DropdownIndicator = (props) =>
    components.DropdownIndicator && (
      <components.DropdownIndicator {...props}>
        <FilledChevronDown />
      </components.DropdownIndicator>
    );

  const formatOptionLabel = ({ label, dataCy }) => (
    <div data-cy={dataCy}>{label}</div>
  );

  return (
    <InputWrap
      inputFieldError={meta.error && meta.touched}
      dropdownFullHeight={dropdownFullHeight}
    >
      {(label || link) && (
        <InputHeader>
          {!!label && <InputLabel htmlFor={field.name}>{label}</InputLabel>}
          {!!link && <InputLink>{link}</InputLink>}
        </InputHeader>
      )}

      <SelectInput
        defaultValue={defaultSelected || options[0]}
        components={{ DropdownIndicator }}
        formatOptionLabel={formatOptionLabel}
        isSearchable={false}
        classNamePrefix="c-select"
        options={options}
        isDisabled={disabled}
        {...field}
        {...props}
        onBlur={(e) => {
          helpers.setTouched(true);
          field.onBlur(e);
        }}
        onChange={(selectedOption) => {
          if (setAddressInline) {
            setAddressInline(selectedOption.value, helpers.setValue);
          } else {
            helpers.setValue(selectedOption);
          }

          if (props.onChange) {
            props.onChange();
          }

          if (selectOption) {
            selectOption(selectedOption);
          }
        }}
        filterOption={createFilter(filterConfig)}
      />
      <ErrorMessage name={field.name}>
        {(errorMessage) => {
          if (typeof errorMessage === 'string') {
            return <InputError>{errorMessage}</InputError>;
          }
          return <InputError>{errorMessage.value}</InputError>;
        }}
      </ErrorMessage>
    </InputWrap>
  );
};

SelectField.propTypes = {
  field: PropTypes.shape({
    name: PropTypes.string,
  }),
  form: PropTypes.shape({}),
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  disabled: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ),
  onChange: PropTypes.func,
  link: PropTypes.node,
  defaultSelected: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  dropdownFullHeight: PropTypes.bool,
  selectOption: PropTypes.func,
  setAddressInline: PropTypes.func,
};

export default SelectField;
